/**
 * TrialExtensionModal
 * The single app-level booking dialog: the auto popup, the banner, the top bar
 * action and the Overview card all open it. Shows the Calendly inline embed, then
 * a confirmation once the person books. Also mounts the offer's app-level effects
 * (clock, booked URL param, once-per-session popup).
 */

import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  useTrialExtension,
  useTrialExtensionAutoPopup,
} from "../../../hooks/useTrialExtension";
import Modal from "../UIElements/Modal";
import { Button } from "../UIElements/Button";
import { Icon } from "../UIElements/Icon";
import CalendlyInlineEmbed from "./CalendlyInlineEmbed";

const TrialExtensionModal = () => {
  const { t } = useTranslation(["billing"]);
  const trial = useTrialExtension();
  const {
    available,
    booked,
    justBooked,
    days,
    bookingUrl,
    fallbackUrl,
    projectKey,
    resolvedProjectCode,
    isModalOpen,
    closeBooking,
    markBooked,
  } = trial;

  useTrialExtensionAutoPopup({
    available,
    booked,
    projectKey,
    resolvedProjectCode,
  });

  const showSuccess = justBooked;
  // The offer can go away while the dialog is open (project switch, trial ended):
  // close it instead of leaving an empty dialog behind.
  const offerGone = isModalOpen && !available && !showSuccess;
  useEffect(() => {
    if (offerGone) closeBooking();
  }, [offerGone, closeBooking]);

  if (!isModalOpen || offerGone) return null;

  return (
    <Modal
      isOpen
      onClose={closeBooking}
      title={t("trialExtension.title", { days })}
      subtitle={
        showSuccess ? undefined : t("trialExtension.modalDescription", { days })
      }
      maxWidth="max-w-3xl"
      closeButtonLabel={t("trialExtension.close")}
    >
      {showSuccess ? (
        <div
          className="flex flex-col items-center gap-3 py-6 text-center"
          role="status"
        >
          <Icon name="CheckCircle" className="text-success h-12 w-12" />
          <h3 className="text-foreground text-lg font-semibold">
            {t("trialExtension.successTitle")}
          </h3>
          <p className="text-muted-foreground max-w-md text-sm">
            {t("trialExtension.successText", { days })}
          </p>
          <Button
            type="button"
            emphasis="solid"
            intent="brand"
            size="touch"
            onClick={closeBooking}
          >
            {t("trialExtension.close")}
          </Button>
        </div>
      ) : (
        <CalendlyInlineEmbed
          embedUrl={bookingUrl}
          fallbackUrl={fallbackUrl}
          onScheduled={markBooked}
        />
      )}
    </Modal>
  );
};

export default TrialExtensionModal;
